import { tierLabel, tierRuleName, type Score, type ScoreStep } from '../lib/api'
import { bandWord } from './BandChip'
import { Toggletip } from './kit'
import '../styles/band-factors.css'

// Why a band is what it is, as the factors that decided it.
//
// # WHY A LIST OF FACTORS AND NOT THE ENGINE'S STEPS
//
// The product officer's review of 21 September asked for "Why this band?" to
// say which factors and rules gave the band, in words a reviewer reads at a
// glance. The engine's own steps are sentences written for an auditor, and the
// panel used to show the first four of them cut at a clause, so a second gate
// that fired was simply not on the screen. This shows the four numbers the
// band matrix reads, then every rule that fired, each by name with one line of
// what it did and its citation behind a hover, then the matrix's result. The
// engine's steps stay whole in the "How the band was reached" drawer, which is
// where somebody checking the arithmetic goes.
//
// Everything here is read off the stored score, so the same component explains
// a band on the vendor's Risk tab and in an assessment's Score panel, and the
// two can never disagree about a score they are both showing.

// A toggletip holds 200 characters. The whole text is in the drawer.
function tip(text: string): string {
  const t = text.trim()
  if (t.length <= 200) return t
  const cut = t.slice(0, 196)
  const space = cut.lastIndexOf(' ')
  return (space > 140 ? cut.slice(0, space) : cut) + '...'
}

// Band codes inside the engine's sentences, as the words the screen uses.
function bandsInWords(text: string): string {
  return text.replace(/\b(LOW|MEDIUM|HIGH|CRITICAL|INSUFFICIENT_EVIDENCE)\b/g, (b) => bandWord(b))
}

// The first sentence of an engine step. The rest is in the drawer.
function firstSentence(detail: string): string {
  const stop = detail.indexOf('. ')
  return stop > 0 ? detail.slice(0, stop + 1) : detail
}

// "Tier 1 Critical" is the label; "Tier 1" is how the result line reads it.
function tierShort(label: string): string {
  const m = /^Tier \d+/.exec(label)
  return m ? m[0] : label
}

export type FiredRule = {
  name: string
  // What the rule did, in a few words: "No better than High". A cap is a
  // floor under the risk, not a ceiling on it: a vendor already Critical stays
  // Critical, so "caps at High" would read as the opposite of what happened.
  effect: string
  // One line of why, in the rule's own words.
  meaning: string
  citation?: string
}

// A gate step reads "KEY caps the band at HIGH (was LOW). Reason." and three
// variants of it. The effect is the first sentence and the reason the rest;
// both are the model's own words, so nothing here restates a rule. The effect
// is read from the step rather than from the score's flags, because a stored
// score carries the steps and not the flags, so a rule that needs a risk
// acceptance still says so on a score read back later.
function gateRule(s: ScoreStep): FiredRule {
  const key = s.gateKey ?? ''
  const detail = s.detail.trim()
  let effect = ''
  let reason = detail
  const caps = /^\S+ caps the band at (\w+)(?: \(was \w+\))?\.\s*(.*)$/s.exec(detail)
  const forces = /^\S+ forces the band to (\w+)\.\s*(.*)$/s.exec(detail)
  const blocks = /^\S+ blocks approval\.\s*(.*)$/s.exec(detail)
  const needs = /^\S+ requires a documented exception[^.]*\.\s*(.*)$/s.exec(detail)
  if (caps) {
    effect = 'No better than ' + bandWord(caps[1] ?? '')
    reason = caps[2] ?? ''
  } else if (forces) {
    effect = 'Sets ' + bandWord(forces[1] ?? '')
    reason = forces[2] ?? ''
  } else if (blocks) {
    effect = 'Blocks approval'
    reason = blocks[1] ?? ''
  } else if (needs) {
    effect = 'Needs a risk acceptance'
    reason = needs[1] ?? ''
  }
  return {
    name: key || 'Rule',
    effect,
    meaning: reason || detail,
    citation: s.citation,
  }
}

const NAMED: Record<string, string> = {
  provisional_ceiling: 'Provisional ceiling',
  coverage_floor: 'Coverage floor',
  risk_type_rollup: 'Risk types',
}

// Every rule that fired, in the order the engine applied them. A ceiling or a
// roll-up that changed nothing is left out: it is in the drawer, and listing
// it here would name it as a reason for a band it did not change.
export function firedRules(score: Score): FiredRule[] {
  const out: FiredRule[] = []
  for (const s of score.explanation) {
    if (s.step === 'gate_fired' || s.gateKey) {
      out.push(gateRule(s))
      continue
    }
    const name = NAMED[s.step]
    if (!name) continue
    if (s.step === 'provisional_ceiling' && /already/.test(s.detail)) continue
    // The roll-up is a rule only when a risk type made the band worse than
    // the assessment's own. When it did not, it changed nothing.
    if (s.step === 'risk_type_rollup') {
      const m = /^Assessment band (\w+)\..*worst of these, (\w+)\.$/s.exec(s.detail.trim())
      if (!m || m[1] === m[2]) continue
    }
    out.push({ name, effect: '', meaning: bandsInWords(firstSentence(s.detail)) })
  }
  return out
}

// The engine's step keys as headings, as written by
// packages/core/scoring/engine.go. An earlier map on the Risk tab named tier,
// matrix, gate and final, which the engine has never written, so its drawer's
// headings fell through to the raw key; the assessment's drawer printed the
// raw key outright. Both read this one now.
const STEP_LABEL: Record<string, string> = {
  inherent_tier: 'Inherent tier',
  control_score: 'Control score',
  band_matrix: 'Band matrix',
  gate_fired: 'Rule fired',
  provisional_ceiling: 'Provisional ceiling',
  coverage_floor: 'Coverage floor',
  risk_type_partition: 'Risk type',
  risk_type_rollup: 'Risk types',
  final_band: 'Result',
}

export function stepLabel(step: string): string {
  return STEP_LABEL[step] ?? step.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

// The rule that set the inherent tier, from the score's own inherent_tier
// step: "Tier 1 Critical. <the rule's reason>". Older scores stored the bare
// ordinal, and read without a rule name.
function tierRule(score: Score): string {
  const step = score.explanation.find((s) => s.step === 'inherent_tier')
  if (!step) return ''
  const stop = step.detail.indexOf('. ')
  if (stop < 0) return ''
  return tierRuleName(step.detail.slice(stop + 2), undefined)
}

function pct(v: number): number {
  return Math.round(v <= 1 ? v * 100 : v)
}

export function BandFactors({ score }: { score: Score }) {
  const label = score.inherentTier ? tierLabel(score.inherentTier) : ''
  const rule = tierRule(score)
  const rules = firedRules(score)
  const control = Math.round(score.controlScore)
  const matrix = score.matrixBand || score.finalBand
  const moved = score.matrixBand && score.finalBand && score.matrixBand !== score.finalBand

  return (
    <div className="bf">
      <dl className="bf-facts">
        <div>
          <dt>Inherent tier</dt>
          <dd>
            {label || <span className="muted">Not classified</span>}
            {rule && <span className="bf-rule">{rule}</span>}
          </dd>
        </div>
        <div>
          <dt>Control score</dt>
          <dd>
            {control}
            <small>/100</small>
          </dd>
        </div>
        <div>
          <dt>Coverage</dt>
          <dd>{pct(score.coveragePct)}%</dd>
        </div>
        <div>
          <dt>Attested</dt>
          <dd>{pct(score.attestationPct)}%</dd>
        </div>
      </dl>

      <div className="bf-rules">
        <h4>Rules that fired</h4>
        {rules.length === 0 ? (
          <p className="muted bf-none">None</p>
        ) : (
          <ul>
            {rules.map((r, i) => (
              <li key={r.name + i}>
                <span className="bf-chip">{r.name}</span>
                {r.effect && <span className="bf-effect">{r.effect}</span>}
                <span className="bf-meaning" title={r.meaning}>
                  {r.meaning}
                </span>
                {r.citation && (
                  <Toggletip hover label={'Citation for ' + r.name}>
                    {tip(r.citation)}
                  </Toggletip>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {score.inherentTier > 0 && (
        <p className="bf-result">
          {tierShort(label)} and a control score of {control} give {bandWord(matrix)}.
          {moved ? ' The rules above make it ' + bandWord(score.finalBand) + '.' : ''}
        </p>
      )}
    </div>
  )
}
